// ─── « Mes éléments » — choisir ce qu'on utilise avec ses jetons ──
// L'app fournit la LISTE des éléments gérables (calendriers, inventaires…) via
// `sources` : le Core ne connaît pas ces collections.
//   sources = [{ cle:'calendrier', label:'Calendriers', emoji:'📅',
//                elements:[{ id, nom, proprietaire, peutEcrire }] }]
// `proprietaire: false` grise le 🎁 : on n'offre que ce qu'on possède (le
// serveur le vérifie de toute façon).
// `peutEcrire: false` grise le ⚙️ : le JETON et l'ACCÈS sont deux couches
// distinctes — choisir « gérer » ne donne pas le droit d'écrire si le
// propriétaire n'a accordé que la lecture. Le droit réel est le MINIMUM des
// deux, la sélection n'écrase jamais l'accès.
//
// La sélection est ORDONNÉE : l'index est la priorité, et c'est elle qui décide
// de ce qui reste accessible quand des jetons disparaissent. L'enregistrement
// passe par le SERVEUR (quota + fenêtre mensuelle) — cf. jetonsService.
import React, { useEffect, useMemo, useState } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, ActivityIndicator } from 'react-native';
import Feuille from '../ui/Feuille';
import LigneSwipe from '../ui/LigneSwipe';
import CalqueChoix, { LigneChoix } from '../ui/CalqueChoix';
import { useUI } from '../theme/PreferencesUI';
import { useT } from '../i18n/LangueContext';
import { useAuth } from '../auth/AuthContext';
import { useEspace } from '../espace/EspaceContext';
import { membresDe } from '../acces/acces';
import { usePeutUtiliser } from './FonctionsContext';
import { JETON_GESTION, JETON_VUE, allouer, etatEcarte, deplacerPriorite, idEntree } from './jetons';
import { choisirElements, messageJetons } from './jetonsService';
import useJetons from './useJetons';

const HIT = { top: 8, bottom: 8, left: 8, right: 8 };
const cleDe = (src, el) => `${src.cle}:${el.id}`;

export default function FeuilleMesElements({ visible, sources = [], cfgFonctions, onFermer }) {
  const UI = useUI();
  const t = useT();
  const s = useMemo(() => makeStyles(UI), [UI]);
  const { user } = useAuth();
  const { espaceActif } = useEspace();
  const peutDonner = usePeutUtiliser()('jetons.don');
  const [selection, setSelection] = useState([]);   // brouillon ordonné
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  // Parcours « Offrir » : élément → bénéficiaire → nature. `donPour` porte
  // l'élément choisi, `donQui` le bénéficiaire ; null = étape précédente.
  const [donPour, setDonPour] = useState(null);
  // Ligne de file dont on choisit le MODE (swipe droit) : ⚙️ configuration ou
  // 👁 visualisation. Calque absolu, jamais une seconde <Modal> (elle figerait
  // la feuille sur natif).
  const [modePour, setModePour] = useState(null);
  const [donQui, setDonQui] = useState(null);

  // Ouverture : on repart de ce qui est enregistré (ordre compris).
  useEffect(() => {
    if (!visible) return;
    setErr('');
    setSelection(Array.isArray(user?.jetonsSelection) ? user.jetonsSelection : []);
  }, [visible, user?.jetonsSelection]);

  // Les offres sont des ENTRÉES de la même file : mon usage et mon offre se
  // priorisent séparément, puisqu'ils coûtent chacun un jeton.
  const dons = selection.filter(x => x.pour);
  // Les jetons RECUS viennent du serveur (ils vivent chez le donneur) : on lit
  // l'etat partage plutot que de le recalculer ici, sinon la feuille et le
  // calendrier pourraient ne pas dire la meme chose.
  const { recus } = useJetons();
  const alloc = allouer(cfgFonctions || {}, user, selection, recus);
  const initiale = JSON.stringify(user?.jetonsSelection || []);
  const modifie = JSON.stringify(selection) !== initiale;

  const natureDe = (cle) => selection.find(x => x.cle === cle && !x.pour)?.nature || null;
  // Choix DIRECT du mode : toucher ⚙️ ou 👁 pose ce mode ; re-toucher le mode
  // actif renonce à l'élément. Plus lisible qu'un cycle où l'on ne sait pas
  // ce que le prochain tap va faire.
  const choisirMode = (cle, mode) => {
    setErr('');
    setSelection(prev => {
      const i = prev.findIndex(x => x.cle === cle && !x.pour);   // MON entrée
      if (i < 0) return [...prev, { cle, nature: mode }];
      if (prev[i].nature === mode) return prev.filter((x, k) => k !== i);  // les offres restent
      const c = [...prev]; c[i] = { ...c[i], nature: mode }; return c;
    });
  };
  const monter = (id) => setSelection(prev => {
    const i = prev.findIndex(x => idEntree(x) === id);
    return i > 0 ? deplacerPriorite(prev, id, i - 1) : prev;
  });

  // Confier / reprendre : effet IMMÉDIAT (pas de brouillon) — ces mouvements
  // sont hors fenêtre mensuelle et doivent pouvoir être défaits tout de suite.
  // Offrir = AJOUTER UNE ENTRÉE à la file (brouillon) : elle prend son rang,
  // se déplace et s'enregistre comme les autres, avec la 💾.
  const confier = (cle, uid, nature) => {
    setErr('');
    // Filet : le calque a pu rester ouvert pendant qu'un autre choix consommait
    // le dernier jeton.
    const libre = nature === JETON_GESTION ? alloc.restants.gestion : alloc.restants.vue;
    if (libre <= 0) { setErr(t('jetons.donSansJeton')); fermerDon(); return; }
    setSelection(prev => (prev.some(x => x.cle === cle && x.pour === uid)
      ? prev : [...prev, { cle, nature, pour: uid }]));
    fermerDon();
  };
  const fermerDon = () => { setDonPour(null); setDonQui(null); };
  const retirerEntree = (id) => setSelection(prev => prev.filter(x => idEntree(x) !== id));
  const donDe = (cle) => dons.find(d => d.cle === cle);
  const nomDe = (cle) => {
    for (const src of sources) {
      const el = (src.elements || []).find(e => cleDe(src, e) === cle);
      if (el) return `${src.emoji || ''} ${el.nom || el.id}`.trim();
    }
    return cle;
  };
  // Bénéficiaires possibles : les membres de l'espace, moi excepté.
  const candidats = membresDe(espaceActif).filter(m => m.uid !== user?.uid);

  const enregistrer = async () => {
    setBusy(true); setErr('');
    try { await choisirElements(selection); onFermer?.(); }
    catch (e) { setErr(messageJetons(e)); }
    finally { setBusy(false); }
  };

  return (
    <Feuille visible={visible} titre={t('jetons.titre')} emoji="🎟️" hauteur={0.82} onFermer={onFermer}
      action={{ icone: '💾', label: t('commun.enregistrer'), disabled: !modifie || busy, onPress: enregistrer }}
      >
      <View style={s.compteur}>
        <Text style={s.compteurTxt}>
          ⚙️ {alloc.restants.gestion} · 👁 {alloc.restants.vue}
        </Text>
        {busy && <ActivityIndicator size="small" color={UI.accent} />}
      </View>
      <Text style={s.aide}>{t('jetons.aide')}</Text>
      {!!dons.length && <Text style={s.aide}>{t('jetons.donsEnCours', { n: dons.length })}</Text>}
      {!!err && <Text style={s.err}>⚠️ {err}</Text>}

      {/* La FILE : une ligne par jeton consomme — mon usage d'un element ET
          chaque offre que j'en fais. C'est ici qu'on ordonne : en cas de perte
          de jetons, le bas de file saute, quoi qu'il represente. */}
      {selection.length > 0 && (
        <View>
          <Text style={s.groupe}>🎟️ {t('jetons.file')}</Text>
          {selection.map((it, i) => {
            const id = idEntree(it);
            const ecarte = alloc.ecartes.includes(id);
            const pour = it.pour ? candidats.find(m => m.uid === it.pour) : null;
            return (
              // Gestes STANDARD de l'app : swipe ← retire de la file, swipe →
              // ouvre le choix du mode. Plus de ✕ par ligne (principe app-wide).
              // Une offre faite a quelqu'un n'a pas de « mode » a choisir : sa
              // nature est fixee au moment du don.
              <LigneSwipe key={id} espacement={0}
                onSupprimer={() => retirerEntree(id)}
                onEditer={it.pour ? undefined : () => setModePour(it.cle)}>
                <View style={[s.ligne, ecarte && s.ligneEcartee]}>
                  <Text style={s.rang}>{i + 1}</Text>
                  <TouchableOpacity hitSlop={HIT} disabled={i === 0} onPress={() => monter(id)}>
                    <Text style={[s.fleche, i === 0 && s.flecheOff]}>▲</Text>
                  </TouchableOpacity>
                  <View style={{ flex: 1 }}>
                    <Text style={s.nom} numberOfLines={1}>{nomDe(it.cle)}</Text>
                    <Text style={s.sous} numberOfLines={1}>
                      {it.pour ? t('jetons.pourQui', { qui: pour?.nom || it.pour.slice(0, 6) }) : t('jetons.pourMoi')}
                      {alloc.retrogrades.includes(id) ? ` · ${t('jetons.retrograde')}` : ''}
                      {ecarte ? ` · ${t('jetons.ecarte')}` : ''}
                    </Text>
                  </View>
                  <Text style={s.pastille}>{it.nature === JETON_GESTION ? '⚙️' : '👁'}</Text>
                </View>
              </LigneSwipe>
            );
          })}
        </View>
      )}

      {sources.map(src => (
        <View key={src.cle}>
          <Text style={s.groupe}>{src.emoji ? `${src.emoji} ` : ''}{src.label}</Text>
          {(src.elements || []).length === 0 && <Text style={s.vide}>{t('jetons.aucunElement')}</Text>}
          {/* Par NOM : l'ordre d'arrivee des documents varie d'un chargement a
              l'autre, et les lignes se deplacaient sous le doigt. (La FILE, en
              haut, garde SON ordre : c'est la priorite.) */}
          {[...(src.elements || [])].sort((a, b) =>
            (a.nom || '').localeCompare(b.nom || '', 'fr', { sensitivity: 'base' })
          ).map(el => {
            const cle = cleDe(src, el);
            const nature = natureDe(cle);
            const confie = recus.some(r => r.cle === cle);
            const ecarte = alloc.ecartes.includes(cle);
            const rang = selection.findIndex(x => x.cle === cle);
            const grace = ecarte ? etatEcarte(cle, user?.jetonsGrace, new Date().toISOString().slice(0, 10)) : null;
            return (
              <View key={cle} style={[s.ligne, ecarte && s.ligneEcartee]}>
                <View style={{ flex: 1 }}>
                  <Text style={s.nom} numberOfLines={1}>{el.nom || el.id}</Text>
                  <Text style={s.sous}>
                    {confie ? t('jetons.confieParAutre')
                      : nature === JETON_GESTION ? t('jetons.enGestion')
                      : nature === JETON_VUE ? t('jetons.enConsultation')
                      : t('jetons.nonRetenu')}
                    {el.peutEcrire === false ? ` · ${t('jetons.accesLecture')}` : ''}
                    {donDe(cle) ? ` · ${t('jetons.aussiOffert', { nature: donDe(cle).nature === JETON_VUE ? '👁' : '⚙️' })}` : ''}
                    {alloc.retrogrades.includes(cle) ? ` · ${t('jetons.retrograde')}` : ''}
                    {grace?.jours != null ? ` · ${t('jetons.joursRestants', { n: grace.jours })}` : ''}
                  </Text>
                </View>
                {/* Choix DIRECT du mode : deux boutons, pas un cycle a deviner.
                    Re-toucher celui qui est actif renonce a l'element. */}
                {!confie && [JETON_GESTION, JETON_VUE].map(n => {
                  // ⚙️ inutile si l'ACCÈS accordé est en lecture : le jeton ne
                  // surclasse pas le droit donné par le propriétaire.
                  const sansEcriture = n === JETON_GESTION && el.peutEcrire === false;
                  return (
                    <TouchableOpacity key={n} hitSlop={HIT}
                      onPress={() => (sansEcriture ? setErr(t('jetons.lectureSeuleAcces')) : choisirMode(cle, n))}>
                      <Text style={[s.pastille, (sansEcriture || nature !== n) && s.pastilleOff]}>
                        {n === JETON_GESTION ? '⚙️' : '👁'}
                      </Text>
                    </TouchableOpacity>
                  );
                })}
                {confie && <Text style={s.pastille}>🎁</Text>}
                {/* Offrir CET element. Le 🎁 reste VISIBLE meme quand c'est
                    interdit — grise, avec le motif au tap : « rien ne s'affiche »
                    est la pire des reponses (regle app-wide). */}
                {!confie && (() => {
                  // Offrir COUTE un jeton (il reste immobilise chez le donneur) :
                  // sans jeton libre, l'offre serait acceptee puis ecartee en bas
                  // de file au moment d'enregistrer, sans que personne comprenne.
                  const plusDeJeton = alloc.restants.gestion <= 0 && alloc.restants.vue <= 0;
                  const interdit = !peutDonner ? t('jetons.donHorsForfait')
                    : el.proprietaire === false ? t('jetons.donProprietaireSeul')
                    : plusDeJeton ? t('jetons.donSansJeton') : null;
                  return (
                    <TouchableOpacity hitSlop={HIT}
                      onPress={() => (interdit ? setErr(interdit) : (setDonPour(cle), setDonQui(null)))}>
                      <Text style={[s.pastille, (interdit || !donDe(cle)) && s.pastilleOff]}>🎁</Text>
                    </TouchableOpacity>
                  );
                })()}
              </View>
            );
          })}
        </View>
      ))}
      <Text style={s.aide}>{t('jetons.mensuelAide')}</Text>
      <Text style={s.aide}>{t('jetons.accesAide')}</Text>

      {/* Swipe DROIT sur une ligne de file : choisir le mode. Calque (jamais une
          seconde <Modal> imbriquee) mais aux regles de modale de l'app. */}
      <CalqueChoix visible={!!modePour} emoji="🎟️" titre={modePour ? nomDe(modePour) : ''}
        onFermer={() => setModePour(null)}>
        <LigneChoix libelle={`⚙️ ${t('jetons.modeConfiguration')}`}
          onPress={() => { choisirMode(modePour, JETON_GESTION); setModePour(null); }} />
        <LigneChoix libelle={`👁 ${t('jetons.modeVisualisation')}`}
          onPress={() => { choisirMode(modePour, JETON_VUE); setModePour(null); }} />
      </CalqueChoix>

      {/* « Offrir » en trois etapes : quel element, a qui, et quoi. Etapes 2 et 3
          dans le meme calque, aux regles de modale de l'app. */}
      <CalqueChoix visible={!!donPour} emoji="🤝"
        titre={donQui ? t('jetons.offrirQuoiDroit') : t('jetons.confierA')}
        aide={donQui ? '' : t('jetons.confierAide')}
        onFermer={fermerDon}>
        {/* Etape 2 : la personne. */}
        {!donQui && (candidats.length === 0
          ? <Text style={s.vide}>{t('jetons.aucunMembre')}</Text>
          : candidats.map(m => (
            <LigneChoix key={m.uid} libelle={m.nom || m.uid.slice(0, 6)}
              disponible={!busy} onPress={() => setDonQui(m.uid)} />
          )))}
        {/* Etape 3 : ce qu'on offre. Offrir un ⚙️ coute un ⚙️, offrir un 👁
            coute un 👁 : chaque option n'apparait que si SON jeton est libre. */}
        {!!donQui && (
          <>
            <LigneChoix libelle={`⚙️ ${t('jetons.offrirGestion')}`}
              disponible={!busy && alloc.restants.gestion > 0}
              onPress={() => confier(donPour, donQui, JETON_GESTION)} />
            <LigneChoix libelle={`👁 ${t('jetons.offrirVue')}`}
              disponible={!busy && alloc.restants.vue > 0}
              onPress={() => confier(donPour, donQui, JETON_VUE)} />
          </>
        )}
      </CalqueChoix>
    </Feuille>
  );
}

const makeStyles = (UI) => StyleSheet.create({
  compteur: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
              backgroundColor: UI.accentTint, borderRadius: 11, paddingHorizontal: 12, paddingVertical: 9 },
  compteurTxt: { fontSize: 14, fontWeight: '900', color: UI.accent },
  aide: { fontSize: 11.5, color: UI.faint, lineHeight: 16, marginTop: 8 },
  err: { fontSize: 12.5, color: UI.danger, fontWeight: '700', marginTop: 8 },
  groupe: { fontSize: 12, fontWeight: '900', color: UI.muted, textTransform: 'uppercase',
            letterSpacing: 0.5, marginTop: 18, marginBottom: 4 },
  vide: { fontSize: 12.5, color: UI.faint, fontStyle: 'italic', paddingVertical: 8 },
  ligne: { flexDirection: 'row', alignItems: 'center', gap: 10, paddingVertical: 11,
           borderTopWidth: 0.5, borderTopColor: UI.border },
  ligneEcartee: { opacity: 0.55 },
  fleche: { fontSize: 13, color: UI.accent, fontWeight: '900' },
  flecheOff: { color: UI.faint },
  nom: { fontSize: 14, fontWeight: '700', color: UI.ink },
  sous: { fontSize: 11.5, color: UI.muted, marginTop: 1 },
  pastille: { fontSize: 16 },
  pastilleOff: { opacity: 0.28 },
  rang: { fontSize: 11, fontWeight: '900', color: UI.faint, minWidth: 14 },
  action: { fontSize: 16 },
});
